import { ChangeDetectorRef, Directive, Input, TemplateRef, ViewContainerRef } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AccessString, AuthService } from 'providers/auth.service';
import { from, of, ReplaySubject } from 'rxjs';
import { map, startWith, switchMap, take, tap, timeoutWith, withLatestFrom } from 'rxjs/operators';

/**
 * Like *ngIf but checks for a specific permission.
 *
 * Usage:
 *
 * `<element *requireAccessAny="'chargingSessions.access'"></element>`
 *
 * Synonym for *ceAccess (which will be deprecated soon)
 */
@Directive({
    selector: '[requireAccessAny]',
})
@UntilDestroy()
export class RequireAccessAnyDirective {
    protected accessString$ = new ReplaySubject<AccessString>(1);

    @Input('requireAccessAny') set accessString(accessString: AccessString) {
        this.accessString$.next(accessString);
    }

    constructor(
        private templateRef: TemplateRef<any>,
        private viewContainerRef: ViewContainerRef,
        private authService: AuthService,
        private cdRef: ChangeDetectorRef
    ) {
        this.viewContainerRef.clear();

        // wait until authed
        const userIsAuthed$ = from(this.authService.userIsAuthed());
        const hasAccess$ = userIsAuthed$.pipe(
            // wait for accessString
            switchMap(() =>
                this.accessString$.pipe(
                    // if has access string, check for permissions
                    switchMap((accessString) => {
                        if (accessString) {
                            return this.authService.hasAccessTo$(accessString).pipe(
                                map((hasAccess) => ({
                                    hasAccess,
                                    accessString,
                                }))
                            );
                        } else {
                            // Empty access string - allow through
                            return of(true).pipe(
                                map((hasAccess) => ({
                                    hasAccess,
                                    accessString,
                                }))
                            );
                        }
                    })
                )
            )
        );

        // warning of incorrect usage
        hasAccess$
            .pipe(
                // wait 5 seconds before issuing console.warn
                timeoutWith(
                    5 * 1000,
                    of(false).pipe(
                        withLatestFrom(this.accessString$.pipe(startWith(undefined))),
                        tap(([_, accessString]) =>
                            console.warn(
                                `Invalid *requireAccessAny directive usage. Access string missing. accessString=${accessString}`
                            )
                        )
                    )
                ),
                take(1),
                untilDestroyed(this)
            )
            .subscribe();

        // act
        hasAccess$.pipe(untilDestroyed(this)).subscribe(
            ({ hasAccess, accessString }) => {
                this.viewContainerRef.clear();
                this.cdRef.markForCheck();

                if (accessString) {
                    // console.debug('*requireAccessAny', accessString, hasAccess);
                }

                // if authorized, show the view
                if (hasAccess) {
                    this.viewContainerRef.createEmbeddedView(this.templateRef);
                } else {
                    this.viewContainerRef.clear();
                }
                this.cdRef.markForCheck();
            },
            (err) => {
                console.error('*requireAccessAny', `Error while checking permissions`, { err });
            }
        );
    }
}
